import type { GetServerSidePropsResult, Redirect } from 'next'
import type { RefObject } from 'react'

import PropTypes from 'prop-types'
import { useContext, useRef } from 'react'
import { useSelector } from 'react-redux'

import { Loading } from 'components/common/molecules/loading'
import { BlockOverlay, HeadWithMetaTags, NavbarV2, PendingReviewOverlay, Toast } from 'containers'
import { AlertDialog } from 'containers/alert-dialog'
import { AuthProviderContext } from 'containers/auth'
import { PublicListing } from 'containers/public-listing'
import { ListingIdProvider } from 'context/listingIdContext'
import { ViewModeProvider } from 'context/viewModeContext'
import { APPROVAL_STATUS, PUBLIC_LISTING_STATUS } from 'enum/information-type-enum'
import env from 'env'
import { getStoreServerSideProps } from 'store'
import { useAppDispatch } from 'store/@hooks'
import { usePreviewPostMessage } from 'store/@hooks/use-preview-post-message'
import { useTrackPageView } from 'store/@hooks/use-tracker'
import { actions as agentProfileActions } from 'store/agent-profile'
import { actions, selectPublicListing, selectSharingMetaProperties } from 'store/public-listing'
import { LOCATION, MAIN_FEATURE } from 'tracker/events'
import { getStatsigInitializeValues } from 'utils/feature-flag/instance'
import { SlugValidator } from 'utils/slug-validator'

interface IPublicPage {
  isPreview?: boolean
  listingId?: string
  isBlocked?: boolean
  isPendingReview?: boolean
  slug?: string
  isReviewMode?: boolean
  isArchived?: boolean
  isDeleted?: boolean
  isOffMarket?: boolean
}

export const EVENT_NAME = {
  USE_EFFECT: 'USE_EFFECT',
  ON_READY: 'ON_READY',
  ON_TOGGLE: 'ON_TOGGLE',
  ON_MESSAGE: 'ON_MESSAGE',
  CALCULATE_HEIGHT: 'CALCULATE_HEIGHT',
}

const PublicListingPage: React.FC<IPublicPage> = ({
  isPreview,
  listingId,
  isBlocked,
  isPendingReview,
  isReviewMode,
  isArchived,
  isDeleted,
  isOffMarket,
  ...props
}) => {
  const dispatch = useAppDispatch()
  const listing = useSelector(selectPublicListing)
  const authContext = useContext(AuthProviderContext)
  const pageContainerRef = useRef<null | HTMLDivElement>(null)
  const metaData = useSelector(selectSharingMetaProperties)

  useTrackPageView({
    eventProperties: {
      location: isReviewMode ? LOCATION.REVIEW_LISTING : LOCATION.PUBLIC_LISTING,
      mainFeature: MAIN_FEATURE.MARKET_LISTING,
    },
    skipCondition: !!isPreview,
  })

  const calculateHeight = (currentRef: RefObject<HTMLDivElement>): number => {
    if (currentRef.current) {
      const FOOTER_HEIGHT = 400
      return currentRef.current.offsetTop + currentRef.current.clientHeight + FOOTER_HEIGHT
    }
    return 0
  }

  usePreviewPostMessage(pageContainerRef, calculateHeight)
  const isPreviewOrReviewMode = isPreview || isReviewMode

  if (isPreviewOrReviewMode && !authContext.isAuthReady) {
    return <Loading />
  }

  const renderListingPageComponent = () => {
    if (isPreview && !listing.slug && props.slug) {
      void dispatch(
        actions.getDraftedListing({
          slug: props.slug,
        }),
      )
      return null
    }

    if (isReviewMode && !listing.slug && props.slug) {
      void dispatch(
        actions.getReviewListing({
          slug: props.slug,
        }),
      )
      return null
    }

    // TODO: [TECH-29034] Implement the copywriting and design for others mode
    if (isBlocked || isArchived || isDeleted || isOffMarket) {
      return <BlockOverlay isOffMarket={isOffMarket} />
    }

    if (isPendingReview && !isReviewMode) {
      return <PendingReviewOverlay />
    }

    return (
      <ViewModeProvider isPreview={isPreview} isReview={isReviewMode}>
        <PublicListing pageContainerRef={pageContainerRef} />
      </ViewModeProvider>
    )
  }

  return (
    <ListingIdProvider listingId={listingId ?? '-'}>
      <>
        <HeadWithMetaTags title={metaData.title} metaContents={metaData} />
        {!isPreview && <NavbarV2 />}
        <Toast />
        <AlertDialog />
        {renderListingPageComponent()}
      </>
    </ListingIdProvider>
  )
}

export const getServerSideProps = getStoreServerSideProps<Record<string, unknown>>((store) => {
  const PREVIEW_QUERY_PARAM = 'preview'
  const REVIEW_MODE_QUERY_PARAM = 'review-mode'

  const redirectToHome: GetServerSidePropsResult<Redirect> = {
    redirect: { destination: env.BASE_URL, permanent: false },
  }

  const createListingPageProps = (
    isPreview: boolean,
    listing: PublicListing,
    isReviewMode: boolean,
  ) => {
    const isPendingReview =
      listing?.approvalStatus === APPROVAL_STATUS.PENDING &&
      listing?.status === PUBLIC_LISTING_STATUS.PUBLISHED
    const isBlocked = listing?.status === PUBLIC_LISTING_STATUS.INACTIVE && !!listing.blockedAt
    const isArchived = listing?.status === PUBLIC_LISTING_STATUS.INACTIVE && !!listing.archivedAt
    const isDeleted = listing?.status === PUBLIC_LISTING_STATUS.INACTIVE && !!listing.deletedAt
    const isOffMarket =
      listing?.status === PUBLIC_LISTING_STATUS.INACTIVE && !!listing.unpublishedAt
    const props = {
      isPreview,
      listingId: listing?.id,
      isBlocked,
      isArchived,
      isDeleted,
      isOffMarket,
      isPendingReview,
      isReviewMode: isPendingReview && isReviewMode,
    }
    return props
  }

  const getPublicListing = async (slug: string): Promise<PublicListing> => {
    const publicListing: PublicListing = await store
      .dispatch(
        actions.getPublicListing({
          slug,
        }),
      )
      .unwrap()
    return publicListing
  }

  const setAgentProfile = (listing: PublicListing): void => {
    /**
     * TODO: TECH-28139 - Recheck serializing error when email or userId is undefined
     * This will be solved if email and userId cannot undefined but existing code can be optional
     */
    store.dispatch(
      agentProfileActions.setAgentProfile({
        email: listing.contactDetails?.email,
        userId: listing.contactDetails?.userId,
      }),
    )
  }

  const validSlug = (slug: string): boolean => {
    const slugValidator = new SlugValidator()
    return slugValidator.isSlugValid(slug)
  }

  const isPublicListing = (listing: PublicListing) => listing.id && listing.slug

  return async (context) => {
    const { params: { slug } = {}, query: { view } = {} } = context
    const parsedSlug: string = (slug || '') as string

    if (!validSlug(parsedSlug)) {
      return redirectToHome
    }

    const initializeValues = await getStatsigInitializeValues()

    const isPreview = view === PREVIEW_QUERY_PARAM
    const isReviewMode = view === REVIEW_MODE_QUERY_PARAM
    if (isPreview || isReviewMode) {
      const splittedSlug = parsedSlug.split('-')
      return {
        props: {
          isPreview,
          listingId: splittedSlug[splittedSlug.length - 1],
          slug: parsedSlug,
          initializeValues,
          isReviewMode,
        },
      }
    }

    try {
      const publicListing: PublicListing = await getPublicListing(parsedSlug)

      if (!isPublicListing(publicListing)) {
        return redirectToHome
      }

      setAgentProfile(publicListing)

      return {
        props: {
          ...createListingPageProps(isPreview, publicListing, isReviewMode),
          initializeValues,
        },
      }
    } catch (e) {
      return { notFound: true }
    }
  }
})

PublicListingPage.propTypes = {
  isPreview: PropTypes.bool,
  listingId: PropTypes.string,
  isBlocked: PropTypes.bool,
  isPendingReview: PropTypes.bool,
  isReviewMode: PropTypes.bool,
}

PublicListingPage.defaultProps = {
  isPreview: false,
  listingId: '-',
  isBlocked: false,
  isPendingReview: false,
  isReviewMode: false,
}

export default PublicListingPage
